import React, { useState, useEffect } from "react";
import axios from "axios";
import { useParams, Link } from "react-router-dom";
import styled from "styled-components";
import { useNavContext } from "../context/nav_context";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import {
  api_v3_get_tournament_by_id,
  api_v3_list_of_players_admin,
  api_v3_get_classification,
  api_v3_get_aoe2_player_info,
  api_v3_list_of_tournaments,
  api_v3_save_groups_draft,
  api_v3_get_groups_draft,
  api_v1_get_hall_of_fame,
  api_v3_update_division_by_id,
  api_v3_update_players_to_group,
  api_v3_delete_matches_by_group,
  api_v3_generate_matches_round_robin,
  PLAYERDESCANSA,
} from "../utils/constants";
import { TfiAngleLeft } from "react-icons/tfi";
import { Loading, Aoe2InfoPopup } from "../components";
import { Modal, Tooltip } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";

const AOE2_RESOURCES = [
  {
    url: "https://aoe2.net/#aoe2de-profile-",
    icon: "aoe2net.png",
    alt: "AoE2 Net",
  },
  {
    url: "https://smurf.new-chapter.eu/check_player?player_id=",
    icon: "smurf.svg",
    alt: "Smurf database",
  },
  {
    url: "https://www.aoe2insights.com/user/",
    icon: "aoe2insights.webp",
    alt: "AoE2 Insights",
  },
  {
    url: "https://aoe2.gg/profile/",
    icon: "aoe2_gg.svg",
    alt: "AoE2.gg",
  },
];



const AdminCreateGroups = () => {
  const { tournamentId } = useParams();
  const { accessToken } = useNavContext();
  const [tournamentInfo, setTournamentInfo] = useState(null);
  const [players, setPlayers] = useState([]);
  
  const [loadingTournament, setLoadingTournament] = useState(false);
  const [loadingClassifications, setLoadingClassifications] = useState(false);
  const [loadingPlayers, setLoadingPlayers] = useState(false);
  const [playerEloMap, setPlayerEloMap] = useState({});
  const [loadingElo, setLoadingElo] = useState({});

  const [tournamentsList, setTournamentsList] = useState([]);
  const [previousTournamentId, setPreviousTournamentId] = useState("");
  const [dynamicPromotions, setDynamicPromotions] = useState([]);
  const [dynamicRelegations, setDynamicRelegations] = useState([]);
  const [prevPlayerDivisions, setPrevPlayerDivisions] = useState({});
  const [loadingPrevious, setLoadingPrevious] = useState(false);

  // DnD State
  const [columns, setColumns] = useState({ unassigned: [] });
  // Unassigned search
  const [searchTerm, setSearchTerm] = useState("");

  // Groups View State
  const [viewMode, setViewMode] = useState("divisions"); // 'divisions' | 'groups'
  const [groupCounts, setGroupCounts] = useState({});
  const [groupColumns, setGroupColumns] = useState({});

  // Modals
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [showInfoPopup, setShowInfoPopup] = useState(false);

  // Publish
  const [showPublishConfirm, setShowPublishConfirm] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [publishTasks, setPublishTasks] = useState([]);
  const [publishDone, setPublishDone] = useState(false);
  const [publishError, setPublishError] = useState(null);

  // Initialize columns when data changes
  useEffect(() => {
    if (!tournamentInfo || !players.length) return;
    
    // Check if columns have already been initialized with data to prevent re-renders wiping state
    if (Object.keys(columns).length > 1) return;

    const cols = { unassigned: [] };
    tournamentInfo.divisions.forEach((div) => {
      cols[div.divisionId] = [];
    });

    // Populate columns
    players.forEach((p) => {
      // we need to place them. Usually we'd map via fetchAllClassifications
      // since that's asynchronous and sets a map, we can rely on that map here
    });

    // To prevent wiping on every tick, we'll do setup inside fetchAllClassifications completion
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tournamentInfo, players]);


  const fetchTournamentInfo = async (tId) => {
    setLoadingTournament(true);
    try {
      const response = await axios.get(api_v3_get_tournament_by_id(tId), {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      setTournamentInfo(response.data);
      return response.data;
    } catch (error) {
      console.error("Error fetching tournament info:", error);
    }
    setLoadingTournament(false);
    return null;
  };

  const fetchPlayerElo = async (playerId) => {
    const cacheKey = `aoe2info_${playerId}`;
    const cachedData = localStorage.getItem(cacheKey);
    if (cachedData) {
      try {
        const { avgElo, timestamp } = JSON.parse(cachedData);
        // 30 minutes = 30 * 60 * 1000 = 1800000 ms
        if (Date.now() - timestamp < 1800000) {
          setPlayerEloMap((prev) => ({ ...prev, [playerId]: avgElo }));
          return;
        }
      } catch (e) {
        // Ignorar el error y volver a cargar
      }
    }

    setLoadingElo((prev) => ({ ...prev, [playerId]: true }));
    try {
      const response = await axios.get(api_v3_get_aoe2_player_info(playerId), {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      const avgElo = response.data.aoe2Info?.avgElo || 0;
      setPlayerEloMap((prev) => ({ ...prev, [playerId]: avgElo }));
      
      localStorage.setItem(cacheKey, JSON.stringify({ avgElo, timestamp: Date.now() }));
    } catch (error) {
      console.error(`Error fetching elo for player ${playerId}:`, error);
    }
    setLoadingElo((prev) => ({ ...prev, [playerId]: false }));
  };

  const fetchPreviousTournamentClassifications = async (prevTId) => {
    if (!prevTId) {
      setDynamicPromotions([]);
      setDynamicRelegations([]);
      return;
    }
    setLoadingPrevious(true);
    setDynamicPromotions([]);
    setDynamicRelegations([]);

    try {
      const prevTInfoRes = await axios.get(api_v3_get_tournament_by_id(prevTId), {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      const prevTInfo = prevTInfoRes.data;

      const newPromotions = [];
      const newRelegations = [];
      const newPrevPlayerDivs = {};

      // 1) Fetch Hall of Fame to get promotions & relegations directly
      try {
        const hofRes = await axios.get(api_v1_get_hall_of_fame(prevTId), {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        if (hofRes.data && hofRes.data.divisions) {
          hofRes.data.divisions.forEach(div => {
            if (div.promotions) {
              div.promotions.forEach(p => newPromotions.push(p.playerId));
            }
            if (div.relegations) {
              div.relegations.forEach(p => newRelegations.push(p.playerId));
            }
          });
        }
      } catch (err) {
        console.error("Error fetching Hall of Fame for previous tournament:", err);
      }

      // 2) Keep fetching classifications to map ALL players to their previous division sequence
      if (prevTInfo && prevTInfo.divisions) {
        for (const division of prevTInfo.divisions) {
          if (!division.groups) continue;
          for (const group of division.groups) {
            try {
              const cRes = await axios.get(
                api_v3_get_classification(division.divisionId, group.groupId),
                { headers: { Authorization: `Bearer ${accessToken}` } }
              );

              const playersData = cRes.data.players;
              if (playersData && playersData.length > 0) {
                // Registrar la división de cada jugador
                playersData.forEach(p => {
                  newPrevPlayerDivs[p.playerId] = division.sequence;
                });
              }
            } catch (err) {
              console.error(`Error fetching classification div ${division.divisionId} group ${group.groupId}:`, err);
            }
          }
        }
      }

      setDynamicPromotions(newPromotions);
      setDynamicRelegations(newRelegations);
      setPrevPlayerDivisions(newPrevPlayerDivs);
    } catch (error) {
      console.error("Error fetching prev tournament info:", error);
    }
    setLoadingPrevious(false);
  };

  useEffect(() => {
    fetchPreviousTournamentClassifications(previousTournamentId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previousTournamentId]);

  const loadAllData = async () => {
    if (!tournamentId) return;
    
    setLoadingTournament(true);
    setLoadingPlayers(true);
    setLoadingClassifications(true);

    try {
      const tourneysRes = await axios.get(api_v3_list_of_tournaments());
      const sortedTourneys = tourneysRes.data.sort((a,b) => new Date(a.startDate) - new Date(b.startDate));
      setTournamentsList(sortedTourneys);
      const currIdx = sortedTourneys.findIndex(t => t.tournamentId === tournamentId);
      
      if (currIdx > 0) {
         setPreviousTournamentId(sortedTourneys[currIdx - 1].tournamentId);
      }
    } catch(err) {
       console.error("Error fetching tournaments list", err);
    }

    const tInfo = await fetchTournamentInfo(tournamentId);

    try {
      // fetch players
      const pRes = await axios.get(api_v3_list_of_players_admin(tournamentId, false), {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      const approvedPlayers = pRes.data.filter((player) =>
        player.tournamentHistory.some(
          (history) => history.tournamentId === tournamentId && history.status === "APPROBED"
        )
      );

      // fetch classifications
      const pDivMap = {};
      if (tInfo && tInfo.divisions) {
        for (const division of tInfo.divisions) {
          for (const group of division.groups) {
            try {
              const cRes = await axios.get(
                api_v3_get_classification(division.divisionId, group.groupId),
                { headers: { Authorization: `Bearer ${accessToken}` } }
              );
              cRes.data.players.forEach((p) => {
                pDivMap[p.playerId] = division.divisionId;
              });
            } catch (err) {
              console.error(err);
            }
          }
        }
      }

      setPlayers(approvedPlayers);
      approvedPlayers.forEach((p) => fetchPlayerElo(p.playerId));

      // fetch draft
      let draftData = null;
      try {
        const draftRes = await axios.get(api_v3_get_groups_draft(tournamentId), {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        draftData = draftRes.data;
      } catch (err) {
        // En caso de que no exista el draft (404) o error
      }

      // Construct Initial DnD Columns
      const cols = { unassigned: [] };
      if (tInfo && tInfo.divisions) {
        tInfo.divisions.forEach((div) => {
          cols[div.divisionId] = [];
        });
      }

      if (draftData && draftData.unassignedPlayers) {
        const playerMap = {};
        approvedPlayers.forEach(p => { playerMap[p.playerId] = p; });
        
        draftData.unassignedPlayers.forEach(pid => {
          if (playerMap[pid]) cols.unassigned.push(playerMap[pid]);
        });
        
        const newGroupCounts = {};
        const newGroupColumns = {};
        draftData.divisions.forEach(divDraft => {
          if (cols[divDraft.divisionId]) {
             divDraft.playerIds.forEach(pid => {
               if (playerMap[pid]) cols[divDraft.divisionId].push(playerMap[pid]);
             });

             if (divDraft.groups && divDraft.groups.length > 0) {
               newGroupCounts[divDraft.divisionId] = divDraft.groups.length;
               divDraft.groups.forEach(g => {
                 const gId = `${divDraft.divisionId}-g${g.sequence}`;
                 newGroupColumns[gId] = g.playerIds
                   .map(pid => playerMap[pid])
                   .filter(p => !!p);
               });
             }
          }
        });
        setGroupCounts(newGroupCounts);
        setGroupColumns(newGroupColumns);
        
        // Catch any player that was approved but not in draft
        approvedPlayers.forEach(p => {
          let found = draftData.unassignedPlayers.includes(p.playerId);
          if (!found) {
             draftData.divisions.forEach(d => {
                if (d.playerIds.includes(p.playerId)) found = true;
             });
          }
          if (!found) cols.unassigned.push(p);
        });
      } else {
        approvedPlayers.forEach((p) => {
          const divId = pDivMap[p.playerId];
          if (divId && cols[divId]) {
            cols[divId].push(p);
          } else {
            cols.unassigned.push(p);
          }
        });
      }

      // Sort unassigned by default (elo or name won't have elo yet, but that's fine)
      setColumns(cols);

    } catch (err) {
      console.error(err);
    }

    setLoadingClassifications(false);
    setLoadingPlayers(false);
    setLoadingTournament(false);
  };

  useEffect(() => {
    loadAllData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tournamentId]);

  const handleReloadElos = () => {
    Object.keys(localStorage).forEach((key) => {
      if (key.startsWith("aoe2info_")) {
        localStorage.removeItem(key);
      }
    });

    players.forEach((p) => {
      fetchPlayerElo(p.playerId);
    });
  };

  const onDragEnd = (result) => {
    const { source, destination, draggableId } = result;

    if (!destination) return;
    if (source.droppableId === destination.droppableId && source.index === destination.index) return;

    if (viewMode === "divisions") {
      const sourceCol = [...columns[source.droppableId]];
      const destCol = source.droppableId === destination.droppableId ? sourceCol : [...columns[destination.droppableId]];

      const sourceIndexInRaw = sourceCol.findIndex((p) => String(p.playerId) === String(draggableId));
      if (sourceIndexInRaw === -1) return;

      const [removed] = sourceCol.splice(sourceIndexInRaw, 1);
      destCol.splice(destination.index, 0, removed);

      setColumns((prev) => ({
        ...prev,
        [source.droppableId]: sourceCol,
        [destination.droppableId]: destCol,
      }));
    } else {
      const sourceCol = [...(groupColumns[source.droppableId] || [])];
      const destCol = source.droppableId === destination.droppableId ? sourceCol : [...(groupColumns[destination.droppableId] || [])];

      const sourceIndexInRaw = sourceCol.findIndex((p) => String(p.playerId) === String(draggableId));
      if (sourceIndexInRaw === -1) return;

      const [removed] = sourceCol.splice(sourceIndexInRaw, 1);
      destCol.splice(destination.index, 0, removed);

      setGroupColumns((prev) => ({
        ...prev,
        [source.droppableId]: sourceCol,
        [destination.droppableId]: destCol,
      }));
    }
  };

  const getAvgElo = (playerId) => {
    return playerEloMap[playerId] || 0;
  };

  const handleAutoFill = () => {
    if (!tournamentInfo || !tournamentInfo.divisions || tournamentInfo.divisions.length === 0) {
      alert("No se cargaron las divisiones o el torneo actual.");
      return;
    }

    const currentDivs = [...tournamentInfo.divisions].sort((a, b) => a.sequence - b.sequence);
    const minSeq = currentDivs[0].sequence;
    const maxSeq = currentDivs[currentDivs.length - 1].sequence;

    const newCols = { unassigned: [] };
    currentDivs.forEach(d => { newCols[d.divisionId] = []; });

    const newPlayersList = [];

    // FASE 1: Asignar Veteranos
    players.forEach(p => {
      const prevSeq = prevPlayerDivisions[p.playerId];

      if (prevSeq !== undefined) {
        let targetSeq = prevSeq;
        if (dynamicPromotions.includes(p.playerId)) {
          targetSeq = prevSeq - 1;
        } else if (dynamicRelegations.includes(p.playerId)) {
          targetSeq = prevSeq + 1;
        }

        // Limitar la secuencia a los topes mínimo/máximo posibles de las divisiones del torneo
        if (targetSeq < minSeq) targetSeq = minSeq;
        if (targetSeq > maxSeq) targetSeq = maxSeq;

        const matchDiv = currentDivs.find(d => d.sequence === targetSeq);
        if (matchDiv) {
          newCols[matchDiv.divisionId].push(p);
        } else {
          newCols.unassigned.push(p);
        }
      } else {
        newPlayersList.push(p);
      }
    });

    // FASE 2: Calcular ELO medio por división basado en los veteranos asignados.
    const divAvgElo = {};
    currentDivs.forEach(div => {
      const divPlayers = newCols[div.divisionId];
      const validPlayers = divPlayers.filter(p => getAvgElo(p.playerId) > 0);
      
      if (validPlayers.length > 0) {
        const sumElo = validPlayers.reduce((sum, p) => sum + getAvgElo(p.playerId), 0);
        divAvgElo[div.divisionId] = sumElo / validPlayers.length;
      } else {
        divAvgElo[div.divisionId] = 0;
      }
    });

    // FASE 3: Distribuir nuevos jugadores (Novatos) donde su elo sea el más cercano.
    newPlayersList.forEach(p => {
      const pElo = getAvgElo(p.playerId);
      let closestDivId = null;
      let minDiff = Infinity;

      currentDivs.forEach(div => {
        const dElo = divAvgElo[div.divisionId];
        const diff = Math.abs(dElo - pElo);
        if (diff < minDiff) {
          minDiff = diff;
          closestDivId = div.divisionId;
        }
      });

      if (closestDivId) {
        newCols[closestDivId].push(p);
      } else {
        newCols.unassigned.push(p);
      }
    });

    // FASE 4: Sort players inside each division by average ELO (highest to lowest)
    Object.keys(newCols).forEach(key => {
      newCols[key].sort((a, b) => getAvgElo(b.playerId) - getAvgElo(a.playerId));
    });

    setColumns(newCols);
  };

  const handleGroupsDraft = () => {
    setViewMode("groups");
  };

  const handleAutoFillGroups = () => {
    if (!tournamentInfo || !tournamentInfo.divisions) return;
    
    const newGroupCounts = { ...groupCounts };
    const newGroupColumns = {};

    tournamentInfo.divisions.forEach(div => {
      const divPlayers = [...(columns[div.divisionId] || [])].sort((a, b) => {
        const eloA = playerEloMap[a.playerId] || 0;
        const eloB = playerEloMap[b.playerId] || 0;
        return eloB - eloA;
      });
      if (divPlayers.length === 0) return;

      let gCount = newGroupCounts[div.divisionId];
      if (!gCount || gCount < 1) {
        gCount = Math.max(1, Math.ceil(divPlayers.length / 8));
        newGroupCounts[div.divisionId] = gCount;
      }

      const groupsArray = Array.from({ length: gCount }, () => []);
      let dir = 1;
      let index = 0;

      divPlayers.forEach(p => {
        groupsArray[index].push(p);
        index += dir;
        if (index === gCount) {
          dir = -1;
          index = gCount - 1;
        } else if (index === -1) {
          dir = 1;
          index = 0;
        }
      });

      groupsArray.forEach((gData, i) => {
        newGroupColumns[`${div.divisionId}-g${i + 1}`] = gData;
      });
    });

    setGroupCounts(newGroupCounts);
    setGroupColumns(newGroupColumns);
  };

  const handleAdjustGroupCount = (divId, delta) => {
    const minGroups = 1;
    const current = groupCounts[divId] || 1;
    const nextCount = Math.max(minGroups, current + delta);
    
    if (nextCount !== current) {
      const newGroupCounts = { ...groupCounts, [divId]: nextCount };
      setGroupCounts(newGroupCounts);
      
      const divPlayers = [...(columns[divId] || [])].sort((a, b) => {
        const eloA = playerEloMap[a.playerId] || 0;
        const eloB = playerEloMap[b.playerId] || 0;
        return eloB - eloA;
      });

      const groupsArray = Array.from({ length: nextCount }, () => []);
      let dir = 1;
      let index = 0;
      divPlayers.forEach(p => {
        groupsArray[index].push(p);
        index += dir;
        if (index === nextCount) {
          dir = -1;
          index = nextCount - 1;
        } else if (index === -1) {
          dir = 1;
          index = 0;
        }
      });

      const updatedColumns = { ...groupColumns };
      Object.keys(updatedColumns).forEach(k => {
        if (k.startsWith(`${divId}-g`)) {
          delete updatedColumns[k];
        }
      });
      groupsArray.forEach((gData, i) => {
        updatedColumns[`${divId}-g${i + 1}`] = gData;
      });
      setGroupColumns(updatedColumns);
    }
  };

  const handleResetDivision = (divisionId) => {
    setColumns((prev) => {
      const divPlayers = prev[divisionId] || [];
      if (divPlayers.length === 0) return prev;
      return {
        ...prev,
        [divisionId]: [],
        unassigned: [...prev.unassigned, ...divPlayers],
      };
    });
  };

  // ─── PUBLISH ───────────────────────────────────────────────────────────────

  const buildPublishTasks = () => {
    if (!tournamentInfo) return [];
    const tasks = [];
    tournamentInfo.divisions.forEach((div) => {
      const gCount = groupCounts[div.divisionId] || 0;
      if (gCount === 0) return;
      // PASO 0: borrar partidos de grupos existentes
      (div.groups || []).forEach((g) => {
        tasks.push({ label: `[Paso 0] Borrando partidos: ${div.name} – ${g.name}`, groupId: g.groupId, status: "pending" });
      });
      // PASO 1: recrear grupos
      tasks.push({ label: `[Paso 1] Recreando grupos: ${div.name}`, divisionId: div.divisionId, status: "pending" });
      // PASO 2 + 3: jugadores y partidos (se añaden dinámicamente tras conocer los groupIds)
      for (let i = 1; i <= gCount; i++) {
        tasks.push({ label: `[Paso 2] Asignando jugadores: ${div.name} – Grupo ${i}`, divisionId: div.divisionId, groupSeq: i, status: "pending" });
        tasks.push({ label: `[Paso 3] Generando partidos: ${div.name} – Grupo ${i}`, divisionId: div.divisionId, groupSeq: i, status: "pending" });
      }
    });
    return tasks;
  };

  // eslint-disable-next-line no-unused-vars
  const updateTask = (idx, status, setTasks) => {
    setTasks((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], status };
      return next;
    });
  };

  const handlePublish = async () => {
    setIsPublishing(true);
    setPublishDone(false);
    setPublishError(null);

    const tasks = buildPublishTasks();
    setPublishTasks(tasks);

    let taskIdx = 0;
    const headers = { Authorization: `Bearer ${accessToken}` };

    // Helper: actualiza el estado de una tarea capturando idx por valor
    const markTask = (idx, status) => {
      setPublishTasks((prev) => {
        const n = [...prev];
        n[idx] = { ...n[idx], status };
        return n;
      });
    };

    try {
      const divisionsToProcess = (tournamentInfo?.divisions || []).filter(
        (div) => (groupCounts[div.divisionId] || 0) > 0
      );

      for (const div of divisionsToProcess) {
        const gCount = groupCounts[div.divisionId];

        // ── PASO 0: borrar partidos de grupos existentes ──────────────────────
        const existingGroups = div.groups || [];
        for (const g of existingGroups) {
          const idx0 = taskIdx;
          markTask(idx0, "running");
          try {
            await axios.delete(api_v3_delete_matches_by_group(g.groupId), { headers });
          } catch (e) {
            // 404 es OK (no hay partidos), tratamos como éxito
          }
          markTask(idx0, "done");
          taskIdx++;
        }

        // ── PASO 1: recrear grupos en la división ─────────────────────────────
        const idx1 = taskIdx;
        markTask(idx1, "running");
        const newGroups = Array.from({ length: gCount }, (_, i) => ({
          name: gCount === 1 ? "Grupo único" : `Grupo ${String.fromCharCode(65 + i)}`,
          type: "REGULAR_LEAGUE",
        }));
        const updatedDiv = { ...div, groups: newGroups };
        await axios.put(api_v3_update_division_by_id(div.divisionId), updatedDiv, { headers });
        markTask(idx1, "done");
        taskIdx++;

        // Recargar torneo para obtener groupIds reales
        const reloaded = await axios.get(api_v3_get_tournament_by_id(tournamentId), { headers });
        const freshDiv = reloaded.data.divisions.find((d) => d.divisionId === div.divisionId);
        const freshGroups = (freshDiv?.groups || []).sort((a, b) => a.name.localeCompare(b.name));

        // ── PASO 2 + 3: jugadores y partidos por grupo ────────────────────────
        for (let i = 1; i <= gCount; i++) {
          const groupKey = `${div.divisionId}-g${i}`;
          const playersInGroup = groupColumns[groupKey] || [];
          const freshGroup = freshGroups[i - 1];
          if (!freshGroup) continue;
          const freshGroupId = freshGroup.groupId;

          // PASO 2: asignar jugadores
          const idx2 = taskIdx;
          markTask(idx2, "running");
          let playerIds = playersInGroup.map((p) => p.playerId);
          if (playerIds.length % 2 !== 0) playerIds.push(PLAYERDESCANSA.playerId);
          await axios.put(api_v3_update_players_to_group(div.divisionId, freshGroupId), playerIds, { headers });
          markTask(idx2, "done");
          taskIdx++;

          // PASO 3: generar partidos round robin
          const idx3 = taskIdx;
          markTask(idx3, "running");
          await axios.put(api_v3_generate_matches_round_robin(tournamentId, div.divisionId, freshGroupId), {}, { headers });
          markTask(idx3, "done");
          taskIdx++;
        }
      }

      setPublishDone(true);
    } catch (err) {
      console.error("Error during publish:", err);
      setPublishError(err?.response?.data?.detail || err.message || "Error desconocido");
      const errIdx = taskIdx;
      setPublishTasks((prev) => {
        const n = [...prev];
        if (errIdx < n.length) n[errIdx] = { ...n[errIdx], status: "error" };
        return n;
      });
    } finally {
      setIsPublishing(false);
    }
  };

  const publishSummary = () => {
    if (!tournamentInfo) return { divCount: 0, groupCount: 0, playerCount: 0 };
    let divCount = 0, groupCount = 0, playerCount = 0;
    tournamentInfo.divisions.forEach((div) => {
      const gCount = groupCounts[div.divisionId] || 0;
      if (gCount === 0) return;
      divCount++;
      groupCount += gCount;
      for (let i = 1; i <= gCount; i++) {
        const gPlayers = groupColumns[`${div.divisionId}-g${i}`] || [];
        playerCount += gPlayers.length;
      }
    });
    return { divCount, groupCount, playerCount };
  };

  // ─── SAVE ───────────────────────────────────────────────────────────────────

  const handleSave = async () => {
    try {
      const payload = {
        tournamentId: tournamentId,
        unassignedPlayers: columns.unassigned.map(p => p.playerId),
        divisions: Object.keys(columns)
          .filter(k => k !== "unassigned")
          .map(divId => {
            const gCount = groupCounts[divId] || 0;
            const groupsDraft = [];
            if (gCount > 0) {
              for (let i = 1; i <= gCount; i++) {
                const gId = `${divId}-g${i}`;
                groupsDraft.push({
                  sequence: i,
                  name: `Grupo ${i}`,
                  playerIds: (groupColumns[gId] || []).map(p => p.playerId)
                });
              }
            }
            return {
              divisionId: divId,
              playerIds: columns[divId].map(p => p.playerId),
              groups: groupsDraft.length > 0 ? groupsDraft : null
            };
          })
      };
      await axios.put(api_v3_save_groups_draft(tournamentId), payload, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      alert("Draft guardado exitosamente!");
    } catch (err) {
      console.error("Error saving draft:", err);
      alert("Error al guardar el draft.");
    }
  };

  if (loadingTournament || loadingPlayers || loadingClassifications) {
    return (
      <Wrapper>
        <Loading />
      </Wrapper>
    );
  }

  const unassignedFiltered = columns.unassigned
    ? columns.unassigned
        .filter((p) => p.username.toLowerCase().includes(searchTerm.toLowerCase()))
        .sort((a, b) => getAvgElo(b.playerId) - getAvgElo(a.playerId))
    : [];

  return (
    <Wrapper>
      <HeaderSection>
        <div className="title">
          <h2>Admin Groups Management</h2>
          <div className="underline"></div>
        </div>
        <div className="previous-selector">
          <span>Torneo Anterior:</span>
          <select 
            value={previousTournamentId} 
            onChange={(e) => setPreviousTournamentId(e.target.value)}
            disabled={loadingPrevious}
          >
            <option value="">-- Ninguno --</option>
            {tournamentsList
              .filter(t => t.tournamentId !== tournamentId)
              .map(t => (
              <option key={t.tournamentId} value={t.tournamentId}>{t.name}</option>
            ))}
          </select>
          {loadingPrevious && <span style={{ color: "#177ddc", fontSize: "13px" }}>Calculando...</span>}
        </div>
        <div className="actions">
          {viewMode === "divisions" ? (
            <>
              <Link to="/admin/" className="btn-back">
                <TfiAngleLeft />
                Admin Home
              </Link>
              <button className="btn-action bg-cyan" onClick={handleGroupsDraft}>GROUPS DRAFT</button>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginLeft: '8px' }}>
                <button className="btn-action bg-gray" onClick={handleReloadElos}>RELOAD ELOS</button>
                <Tooltip title="La info de elo se cachea durante 30 minutos">
                  <InfoCircleOutlined style={{ color: "#a6a6a6", cursor: "pointer", fontSize: '16px' }} />
                </Tooltip>
              </div>
              <button className="btn-action bg-yellow" onClick={handleAutoFill}>AUTO-FILL</button>
              <button className="btn-action bg-yellow" onClick={handleSave}>GUARDAR</button>
            </>
          ) : (
            <>
              <button className="btn-action bg-gray" onClick={() => setViewMode("divisions")}>
                <TfiAngleLeft /> VOLVER A DIVISIÓN
              </button>
              <button className="btn-action bg-cyan" onClick={handleAutoFillGroups}>AUTO-FILL</button>
              <button className="btn-action bg-yellow" onClick={handleSave}>GUARDAR GROUPS</button>
              <button className="btn-action bg-red" onClick={() => setShowPublishConfirm(true)}>🚀 PUBLISH</button>
            </>
          )}
        </div>
      </HeaderSection>

      <DragDropContext onDragEnd={onDragEnd}>
        <BoardContainer>
          {viewMode === "divisions" ? (
            <>
              {/* LEFT SIDEBAR: UNASSIGNED PLAYERS */}
              <Sidebar>
            <div className="sidebar-header">
              <h3>PLAYER ({unassignedFiltered.length})</h3>
              <input 
                type="text" 
                placeholder="Search..." 
                value={searchTerm} 
                onChange={(e) => setSearchTerm(e.target.value)} 
                className="search-input"
              />
            </div>

            <Droppable droppableId="unassigned">
              {(provided, snapshot) => (
                <PlayerListContainer
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  $isDraggingOver={snapshot.isDraggingOver}
                >
                  {unassignedFiltered.map((player, index) => {
                    return (
                      <Draggable key={player.playerId} draggableId={player.playerId} index={index}>
                        {(provided, snapshot) => (
                          <PlayerCard
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            $isDragging={snapshot.isDragging}
                          >
                            {prevPlayerDivisions[player.playerId] !== undefined && (
                              <Tooltip title="Participó en el torneo anterior">
                                <ParticipationDot />
                              </Tooltip>
                            )}
                            <span className="player-name">{player.username}</span>
                            <span className="player-elo">
                               {loadingElo[player.playerId] ? "..." : getAvgElo(player.playerId)}
                            </span>
                            <div className="icons">
                              {dynamicPromotions.includes(player.playerId) && (
                                <img src="https://alexocampos.github.io/liga-espanhola-aoe2-static/assets/promotion.png" alt="promotion" width="16"/>
                              )}
                              {dynamicRelegations.includes(player.playerId) && (
                                <img src="https://alexocampos.github.io/liga-espanhola-aoe2-static/assets/relegation.png" alt="relegation" width="16"/>
                              )}
                              <InfoCircleOutlined
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedPlayer(player);
                                  setShowInfoPopup(true);
                                }}
                              />
                            </div>
                          </PlayerCard>
                        )}
                      </Draggable>
                    );
                  })}
                  {provided.placeholder}
                </PlayerListContainer>
              )}
            </Droppable>
          </Sidebar>

          {/* MAIN GRID: DIVISIONS */}
          <DivisionsGrid>
            {tournamentInfo?.divisions.map((division) => {
              const divPlayers = columns[division.divisionId] || [];
              return (
                <DivisionColumn key={division.divisionId}>
                  <div className="div-header">
                     <span>{division.name} ({divPlayers.length})</span>
                     <button className="btn-reset" onClick={() => handleResetDivision(division.divisionId)}>Reset</button>
                  </div>
                  <Droppable droppableId={division.divisionId}>
                    {(provided, snapshot) => (
                      <DivisionBody
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        $isDraggingOver={snapshot.isDraggingOver}
                      >
                         {divPlayers.map((player, index) => (
                           <Draggable key={player.playerId} draggableId={player.playerId} index={index}>
                              {(provided, snapshot) => (
                                <PlayerCard
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  {...provided.dragHandleProps}
                                  $isDragging={snapshot.isDragging}
                                >
                                  {prevPlayerDivisions[player.playerId] !== undefined && (
                                    <Tooltip title="Participó en el torneo anterior">
                                      <ParticipationDot />
                                    </Tooltip>
                                  )}
                                  <span className="player-name">{player.username}</span>
                                  <span className="player-elo">
                                    {loadingElo[player.playerId] ? "..." : getAvgElo(player.playerId)}
                                  </span>
                                  <div className="icons">
                                    {dynamicPromotions.includes(player.playerId) && (
                                      <img src="https://alexocampos.github.io/liga-espanhola-aoe2-static/assets/promotion.png" alt="promotion" width="16"/>
                                    )}
                                    {dynamicRelegations.includes(player.playerId) && (
                                      <img src="https://alexocampos.github.io/liga-espanhola-aoe2-static/assets/relegation.png" alt="relegation" width="16"/>
                                    )}
                                    <InfoCircleOutlined
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setSelectedPlayer(player);
                                        setShowInfoPopup(true);
                                      }}
                                    />
                                  </div>
                                </PlayerCard>
                              )}
                           </Draggable>
                         ))}
                         {provided.placeholder}
                      </DivisionBody>
                    )}
                  </Droppable>
                </DivisionColumn>
              );
            })}
          </DivisionsGrid>
            </>
          ) : (
            <GroupsGrid>
              {tournamentInfo?.divisions.map((division) => {
                const divPlayers = columns[division.divisionId] || [];
                if (divPlayers.length === 0) return null;
                const gCount = groupCounts[division.divisionId] || 1;
                
                return (
                  <DivisionColumn key={division.divisionId} style={{minWidth: '300px', width: '300px', marginBottom: '0', display: 'flex', flexDirection: 'column', maxHeight: '100%'}}>
                    <div className="div-header" style={{display: 'flex', justifyContent: 'space-between', flexShrink: 0}}>
                      <span>{division.name} ({divPlayers.length})</span>
                      <div style={{display: 'flex', gap: '4px', alignItems: 'center'}}>
                        <button className="btn-reset" onClick={() => handleAdjustGroupCount(division.divisionId, -1)} disabled={gCount <= 1} style={{padding: '2px 6px'}}>-</button>
                        <span style={{fontSize: '11px'}}>{gCount} G</span>
                        <button className="btn-reset" onClick={() => handleAdjustGroupCount(division.divisionId, 1)} style={{padding: '2px 6px'}}>+</button>
                      </div>
                    </div>
                    <div style={{display: 'flex', flexDirection: 'column', gap: '16px', padding: '12px', overflowY: 'auto', flex: 1}}>
                      {Array.from({ length: gCount }).map((_, i) => {
                        const groupId = `${division.divisionId}-g${i + 1}`;
                        const gPlayers = groupColumns[groupId] || [];
                        return (
                          <div key={groupId} style={{background: '#1f1f1f', border: '1px solid #303030', borderRadius: '8px'}}>
                            <div style={{padding: '6px 8px', background: '#262626', borderBottom: '1px solid #303030', fontSize: '12px', fontWeight: '500', color: '#fff'}}>Grupo {i + 1} ({gPlayers.length})</div>
                            <Droppable droppableId={groupId}>
                              {(provided, snapshot) => (
                                <DivisionBody
                                  ref={provided.innerRef}
                                  {...provided.droppableProps}
                                  $isDraggingOver={snapshot.isDraggingOver}
                                  style={{padding: '8px', minHeight: '60px', gridTemplateColumns: '1fr'}}
                                >
                                  {gPlayers.map((player, index) => (
                                    <Draggable key={player.playerId} draggableId={player.playerId} index={index}>
                                      {(provided, snapshot) => (
                                        <PlayerCard
                                          ref={provided.innerRef}
                                          {...provided.draggableProps}
                                          {...provided.dragHandleProps}
                                          $isDragging={snapshot.isDragging}
                                        >
                                          {prevPlayerDivisions[player.playerId] !== undefined && (
                                            <Tooltip title="Participó en el torneo anterior">
                                              <ParticipationDot />
                                            </Tooltip>
                                          )}
                                          <span className="player-name">{player.username}</span>
                                          <span className="player-elo">
                                            {loadingElo[player.playerId] ? "..." : getAvgElo(player.playerId)}
                                          </span>
                                          <div className="icons">
                                            {dynamicPromotions.includes(player.playerId) && (
                                              <img src="https://alexocampos.github.io/liga-espanhola-aoe2-static/assets/promotion.png" alt="promotion" width="16"/>
                                            )}
                                            {dynamicRelegations.includes(player.playerId) && (
                                              <img src="https://alexocampos.github.io/liga-espanhola-aoe2-static/assets/relegation.png" alt="relegation" width="16"/>
                                            )}
                                            <InfoCircleOutlined
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                setSelectedPlayer(player);
                                                setShowInfoPopup(true);
                                              }}
                                            />
                                          </div>
                                        </PlayerCard>
                                      )}
                                    </Draggable>
                                  ))}
                                  {provided.placeholder}
                                </DivisionBody>
                              )}
                            </Droppable>
                          </div>
                        );
                      })}
                    </div>
                  </DivisionColumn>
                );
              })}
            </GroupsGrid>
          )}
        </BoardContainer>
      </DragDropContext>

      {/* INFO POPUP MODAL */}
      <Modal
        className="dark-theme-modal"
        style={{ top: 20, maxWidth: "90vw", width: "90vw" }}
        centered
        width="90vw"
        styles={{
          body: { background: "#141414", padding: "24px", color: "#fff" },
          header: { background: "#141414", borderBottom: "1px solid #303030" },
          mask: { backgroundColor: "rgba(0, 0, 0, 0.85)" },
          content: { background: "#141414", borderRadius: "8px", boxShadow: "0 0 8px rgba(0,0,0,0.3)" },
        }}
        title={null}
        open={showInfoPopup}
        onCancel={() => {
          setSelectedPlayer(null);
          setShowInfoPopup(false);
        }}
        footer={null}
      >
        {selectedPlayer && (
          <Aoe2InfoPopup setOpenPopup={setShowInfoPopup} user={selectedPlayer} resources={AOE2_RESOURCES} />
        )}
      </Modal>

      {/* PUBLISH CONFIRMATION MODAL */}
      <Modal
        className="dark-theme-modal"
        centered
        width={560}
        styles={{
          body: { background: "#141414", padding: "28px", color: "#fff" },
          header: { background: "#141414", borderBottom: "1px solid #303030" },
          mask: { backgroundColor: "rgba(0, 0, 0, 0.88)" },
          content: { background: "#141414", borderRadius: "12px", boxShadow: "0 8px 32px rgba(0,0,0,0.6)" },
        }}
        title={<span style={{ color: "#fff", fontSize: "16px", fontWeight: 600 }}>🚀 Publicar grupos del torneo</span>}
        open={showPublishConfirm}
        onCancel={() => { if (!isPublishing) { setShowPublishConfirm(false); setPublishTasks([]); setPublishDone(false); setPublishError(null); } }}
        footer={null}
        closable={!isPublishing}
        maskClosable={!isPublishing}
      >
        {!isPublishing && !publishDone && !publishError && publishTasks.length === 0 && (() => {
          const { divCount, groupCount, playerCount } = publishSummary();
          return (
            <PublishConfirmContent>
              <div className="warning-banner">
                ⚠️ Esta acción <strong>borrará todos los grupos y partidos existentes</strong> y los recreará desde el draft actual.
              </div>
              <div className="summary">
                <div className="summary-item">
                  <span className="label">Divisiones a procesar</span>
                  <span className="value">{divCount}</span>
                </div>
                <div className="summary-item">
                  <span className="label">Grupos totales</span>
                  <span className="value">{groupCount}</span>
                </div>
                <div className="summary-item">
                  <span className="label">Jugadores asignados</span>
                  <span className="value">{playerCount}</span>
                </div>
              </div>
              <p className="note">Los grupos con número impar de jugadores recibirán automáticamente el jugador <strong>DESCANSA</strong>.</p>
              <div className="actions">
                <button className="btn-cancel" onClick={() => setShowPublishConfirm(false)}>Cancelar</button>
                <button className="btn-confirm" onClick={() => { handlePublish(); }}>Confirmar y Publicar</button>
              </div>
            </PublishConfirmContent>
          );
        })()}

        {(isPublishing || publishDone || publishError || publishTasks.length > 0) && (
          <PublishProgressContent>
            {(() => {
              const done = publishTasks.filter(t => t.status === "done").length;
              const total = publishTasks.length;
              const pct = total > 0 ? Math.round((done / total) * 100) : 0;
              return (
                <>
                  <div className="progress-header">
                    <span>{publishDone ? "✅ Proceso completado" : publishError ? "❌ Error durante el proceso" : "⏳ Publicando..."}</span>
                    <span className="pct">{pct}%</span>
                  </div>
                  <div className="progress-bar-track">
                    <div className="progress-bar-fill" style={{ width: `${pct}%`, background: publishError ? "#cf1322" : publishDone ? "#52c41a" : "#177ddc" }} />
                  </div>
                  <div className="task-list">
                    {publishTasks.map((t, i) => (
                      <div key={i} className={`task-item task-${t.status}`}>
                        <span className="task-icon">
                          {t.status === "done" && "✅"}
                          {t.status === "running" && "⏳"}
                          {t.status === "error" && "❌"}
                          {t.status === "pending" && "○"}
                        </span>
                        <span className="task-label">{t.label}</span>
                      </div>
                    ))}
                  </div>
                  {publishError && <div className="error-msg">Error: {publishError}</div>}
                  {(publishDone || publishError) && (
                    <div className="close-row">
                      <button className="btn-cancel" onClick={() => { setShowPublishConfirm(false); setPublishTasks([]); setPublishDone(false); setPublishError(null); }}>Cerrar</button>
                    </div>
                  )}
                </>
              );
            })()}
          </PublishProgressContent>
        )}
      </Modal>
    </Wrapper>
  );
};

export default AdminCreateGroups;

/* ------------- STYLED COMPONENTS ------------- */

const Wrapper = styled.section`
  width: 100%;
  max-width: 100%;
  padding: 24px 2%;
`;

const HeaderSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  border-bottom: 1px solid #303030;
  padding-bottom: 16px;
  flex-wrap: wrap;
  gap: 16px;

  @media (max-width: 900px) {
    flex-direction: column;
    align-items: stretch;
  }

  .title {
    h2 {
      color: #fff;
      font-size: 24px;
      font-weight: 600;
      margin: 0;
    }
    .underline {
      display: none;
    }
  }

  .previous-selector {
    display: flex;
    align-items: center;
    gap: 8px;
    flex: 1;
    padding-left: 24px;
    
    span {
      color: #fff;
      font-size: 14px;
      font-weight: 600;
      white-space: nowrap;
    }

    select {
      padding: 6px 10px;
      border-radius: 6px;
      background: #1f1f1f;
      color: #fff;
      border: 1px solid #434343;
      font-size: 14px;
      min-width: 200px;
      width: 100%;
    }

    @media (max-width: 900px) {
      padding-left: 0;
      flex-direction: column;
      align-items: flex-start;
      margin-bottom: 8px;
    }
  }

  .actions {
    display: flex;
    gap: 16px;
    flex-wrap: wrap;
    justify-content: space-between;

    @media (max-width: 900px) {
      width: 100%;
    }

    .btn-back {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 4px 15px;
      border-radius: 6px;
      background: transparent;
      color: #177ddc;
      border: 1px solid #177ddc;
      text-decoration: none;
      font-size: 14px;
      transition: all 0.3s cubic-bezier(0.645, 0.045, 0.355, 1);

      &:hover {
        background: rgba(23, 125, 220, 0.1);
        color: #40a9ff;
        border-color: #40a9ff;
      }
    }

    .btn-action {
      padding: 4px 15px;
      border-radius: 6px;
      cursor: pointer;
      font-size: 14px;
      transition: all 0.3s cubic-bezier(0.645, 0.045, 0.355, 1);
      
      &.bg-yellow {
        background: #177ddc;
        color: #fff;
        border: 1px solid #177ddc;
      }

      &.bg-cyan {
        background: #13c2c2;
        color: #fff;
        border: 1px solid #13c2c2;
      }

      &.bg-gray {
        background: #434343;
        color: #fff;
        border: 1px solid #434343;
      }

      &.bg-red {
        background: #cf1322;
        color: #fff;
        border: 1px solid #cf1322;
        font-weight: 700;
        letter-spacing: 0.5px;

        &:hover {
          background: #ff4d4f !important;
          border-color: #ff4d4f !important;
        }
      }

      &:hover {
        background: #40a9ff;
        border-color: #40a9ff;
      }
    }
  }
`;

const BoardContainer = styled.div`
  display: flex;
  gap: 24px;
  height: calc(100vh - 200px);

  @media (max-width: 900px) {
    flex-direction: column;
    height: auto;
  }
`;

const GroupsGrid = styled.div`
  flex: 1;
  display: flex;
  flex-direction: row;
  gap: 20px;
  overflow-x: auto;
  overflow-y: hidden;
  width: 100%;
  padding-bottom: 8px;
  
  &::-webkit-scrollbar {
    height: 8px;
    width: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: #434343;
    border-radius: 4px;
  }
`;

const Sidebar = styled.div`
  width: 300px;
  display: flex;
  flex-direction: column;
  background: #141414;
  border: 1px solid #303030;
  border-radius: 8px;
  padding: 16px;

  @media (max-width: 900px) {
    width: 100%;
    max-height: 400px;
  }

  .sidebar-header {
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-bottom: 16px;

    h3 {
      font-size: 16px;
      margin: 0;
      color: #fff;
      font-weight: 600;
    }

    .search-input {
      background: #1f1f1f;
      border: 1px solid #434343;
      color: #fff;
      border-radius: 6px;
      padding: 6px 11px;
      width: 100%;
      font-size: 14px;
      transition: all 0.3s;
      
      &::placeholder {
        color: rgba(255, 255, 255, 0.3);
      }

      &:focus {
        outline: none;
        border-color: #177ddc;
        box-shadow: 0 0 0 2px rgba(23, 125, 220, 0.2);
      }
    }
  }
`;

const PlayerListContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  padding-right: 8px;
  background: ${(props) => (props.$isDraggingOver ? "rgba(23, 125, 220, 0.08)" : "transparent")};
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  transition: background 0.3s;

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: #434343;
    border-radius: 3px;
  }
`;

const DivisionsGrid = styled.div`
  flex: 1;
  column-width: 340px;
  column-gap: 24px;
  overflow-y: auto;
  padding-right: 8px;
  padding-bottom: 16px;
  
  @media (max-width: 900px) {
    column-width: auto;
    display: flex;
    flex-direction: column;
    gap: 24px;
    overflow-y: visible;
  }
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: #434343;
    border-radius: 3px;
  }
`;

const DivisionColumn = styled.div`
  background: #141414;
  border: 1px solid #303030;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  break-inside: avoid;
  margin-bottom: 24px;

  .div-header {
    background: #1f1f1f;
    padding: 8px 12px;
    font-weight: 600;
    font-size: 13px;
    text-transform: uppercase;
    border-bottom: 1px solid #303030;
    display: flex;
    justify-content: space-between;
    align-items: center;
    color: #fff;

    .btn-reset {
      background: transparent;
      border: 1px solid #434343;
      color: #a6a6a6;
      border-radius: 4px;
      padding: 2px 8px;
      font-size: 11px;
      cursor: pointer;
      transition: all 0.3s;
      
      &:hover {
        color: #ff4d4f;
        border-color: #ff4d4f;
        background: rgba(255, 77, 79, 0.1);
      }
    }
  }
`;

const DivisionBody = styled.div`
  flex: 1;
  padding: 12px;
  background: ${(props) => (props.$isDraggingOver ? "rgba(23, 125, 220, 0.08)" : "transparent")};
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 6px;
  min-height: 100px;
  align-content: start;
  transition: background 0.3s;
`;

const PlayerCard = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 10px;
  background: ${(props) => (props.$isDragging ? "#262626" : "#1f1f1f")};
  border: 1px solid ${(props) => (props.$isDragging ? "#177ddc" : "#434343")};
  border-radius: 6px;
  transition: all 0.3s;
  user-select: none;
  
  &:hover {
    background: #262626;
    border-color: #177ddc;
  }

  .player-name {
    font-weight: 500;
    color: #fff;
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 12px;
  }

  .player-elo {
    color: rgba(255, 255, 255, 0.65);
    font-size: 12px;
    margin: 0 10px;
    width: 35px;
    text-align: right;
  }

  .icons {
    display: flex;
    align-items: center;
    gap: 6px;
    color: #177ddc;
    cursor: pointer;
    font-size: 14px;
    
    svg:hover {
      color: #40a9ff;
    }
  }
`;

const ParticipationDot = styled.span`
  width: 8px;
  height: 8px;
  background-color: #52c41a;
  border-radius: 50%;
  margin-right: 8px;
  display: inline-block;
  flex-shrink: 0;
  cursor: help;
`;

const PublishConfirmContent = styled.div`
  .warning-banner {
    background: rgba(207, 19, 34, 0.15);
    border: 1px solid #cf1322;
    border-radius: 8px;
    padding: 14px 16px;
    font-size: 14px;
    color: #ff7875;
    margin-bottom: 20px;
    line-height: 1.5;

    strong {
      color: #ff4d4f;
    }
  }

  .summary {
    display: flex;
    gap: 12px;
    margin-bottom: 16px;
  }

  .summary-item {
    flex: 1;
    background: #1f1f1f;
    border: 1px solid #303030;
    border-radius: 8px;
    padding: 12px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;

    .label {
      font-size: 11px;
      color: rgba(255,255,255,0.45);
      text-align: center;
    }

    .value {
      font-size: 24px;
      font-weight: 700;
      color: #fff;
    }
  }

  .note {
    font-size: 12px;
    color: rgba(255,255,255,0.45);
    margin-bottom: 24px;
    line-height: 1.6;

    strong { color: rgba(255,255,255,0.65); }
  }

  .actions {
    display: flex;
    justify-content: flex-end;
    gap: 12px;
  }

  .btn-cancel {
    padding: 6px 18px;
    background: transparent;
    border: 1px solid #434343;
    border-radius: 6px;
    color: rgba(255,255,255,0.65);
    cursor: pointer;
    font-size: 14px;
    transition: all 0.2s;
    &:hover { border-color: #fff; color: #fff; }
  }

  .btn-confirm {
    padding: 6px 18px;
    background: #cf1322;
    border: 1px solid #cf1322;
    border-radius: 6px;
    color: #fff;
    cursor: pointer;
    font-size: 14px;
    font-weight: 600;
    transition: all 0.2s;
    &:hover { background: #ff4d4f; border-color: #ff4d4f; }
  }
`;

const PublishProgressContent = styled.div`
  .progress-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 10px;
    font-size: 14px;
    font-weight: 600;
    color: #fff;

    .pct {
      font-size: 20px;
      font-weight: 700;
      color: #177ddc;
    }
  }

  .progress-bar-track {
    height: 8px;
    background: #1f1f1f;
    border-radius: 4px;
    overflow: hidden;
    margin-bottom: 18px;
    border: 1px solid #303030;
  }

  .progress-bar-fill {
    height: 100%;
    border-radius: 4px;
    transition: width 0.4s ease, background 0.4s ease;
  }

  .task-list {
    max-height: 340px;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 6px;
    margin-bottom: 16px;
    padding-right: 4px;

    &::-webkit-scrollbar { width: 5px; }
    &::-webkit-scrollbar-thumb { background: #434343; border-radius: 3px; }
  }

  .task-item {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 6px 10px;
    border-radius: 6px;
    font-size: 12px;
    transition: background 0.2s;

    &.task-pending { color: rgba(255,255,255,0.3); background: transparent; }
    &.task-running { color: #fff; background: rgba(23,125,220,0.1); }
    &.task-done    { color: rgba(255,255,255,0.55); background: transparent; }
    &.task-error   { color: #ff4d4f; background: rgba(207,19,34,0.1); }

    .task-icon { font-size: 13px; flex-shrink: 0; }
    .task-label { flex: 1; }
  }

  .error-msg {
    background: rgba(207,19,34,0.15);
    border: 1px solid #cf1322;
    border-radius: 6px;
    padding: 10px 14px;
    font-size: 13px;
    color: #ff7875;
    margin-bottom: 14px;
  }

  .close-row {
    display: flex;
    justify-content: flex-end;
  }

  .btn-cancel {
    padding: 6px 18px;
    background: transparent;
    border: 1px solid #434343;
    border-radius: 6px;
    color: rgba(255,255,255,0.65);
    cursor: pointer;
    font-size: 14px;
    transition: all 0.2s;
    &:hover { border-color: #fff; color: #fff; }
  }
`;

